import React, { useCallback, useMemo, useRef } from 'react';

import { AgentActivity } from '../../hooks/useAgentStatus';
import { useAutoReconnect } from '../../hooks/useAutoReconnect';
import type { PlanningRunNoticeKind } from '../../hooks/usePlanningRunFailure';
import { ChatMessage } from '../../services/chatService';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProject } from '../../store/slices/planningSlice';
import { AgentLogModal } from '../AgentLogModal';
import { ActiveTasksDock } from '../AiOverviewPanel/ActiveTasksDock';
import { StickyPlanningActions } from '../AiOverviewPanel/StickyPlanningActions';
import { useActionMirrorRegion } from '../AiOverviewPanel/useActionMirrorRegion';

import { ChatBoxInputArea } from './ChatBoxInputArea';
import { ErrorBanner } from './ChatBoxMessages';
import { ChatQuotaQueue } from './ChatQuotaQueue';
import { DocProcessingBar } from './DocProcessingBar';
import DocUploadChoiceCard from './DocUploadChoiceCard';
import { MessagesContainer } from './MessagesContainer';
import { PlanningFailureBanner } from './PlanningFailureBanner';
import { useDocUploadChoice } from './useDocProcessing';

type ChatBoxLayoutProps = {
  projectId: string | null;
  sessionId?: string | null;
  title?: string;
  messages: ChatMessage[];
  userDisplayName: string;
  isLoading: boolean;
  error: string | null;
  projectLoadError: string | null;
  isProjectBootstrapping: boolean;
  isProjectUnavailable: boolean;
  isAiTyping: boolean;
  streamingDisplay: string;
  currentStreamingMsgId?: string | null;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onInputChange: (val: string) => void;
  onSend: (val: string, mediaUrls?: string[]) => void;
  onScroll: () => void;
  activeAgents: Record<string, AgentActivity>;
  isPlanningRunning: boolean;
  planningFailureMessage?: string | null;
  planningFailureKind?: PlanningRunNoticeKind | null;
  onDismissPlanningFailure?: () => void;
  onPlanningStillWorking?: () => void;
  isLogModalOpen: boolean;
  onCloseLogModal: () => void;
  onOpenLogModal: () => void;
  hasTodoTasks: boolean;
  todoTaskCount: number;
  isReadOnly?: boolean;
  isSessionResolving?: boolean;
  sessionLoadFailed?: boolean;
};

const useProjectReconnect = (projectId: string | null, sessionId: string | null | undefined, error: string | null) => {
  const dispatch = useAppDispatch();
  const onRetry = useCallback(
    () => projectId ? dispatch(fetchProject({ projectId, sessionId: sessionId ?? null })).unwrap() : Promise.resolve(),
    [dispatch, projectId, sessionId],
  );

  return useAutoReconnect({
    error,
    onRetry,
    enabled: !!projectId,
    scopeKey: JSON.stringify([projectId, sessionId ?? null]),
  });
};

/** Renders the Replace/Merge choice card when a new doc is uploaded to a project with an existing SRD. */
function DocUploadChoiceWrapper({ sessionId }: { sessionId?: string | null }): React.ReactElement | null {
  const [choice, dismiss] = useDocUploadChoice();

  if (!choice) return null;

  return (
    <DocUploadChoiceCard
      filename={choice.filename}
      jobKey={choice.jobKey}
      projectId={choice.projectId}
      sessionId={sessionId}
      recommended={choice.recommended}
      onDone={dismiss}
    />
  );
}

const isChatInputLoading = (props: ChatBoxLayoutProps) => (
  props.isLoading
  || props.isProjectUnavailable
  || props.isProjectBootstrapping
  || Boolean(props.isSessionResolving)
  || Boolean(props.sessionLoadFailed)
);

const noop = () => {};

/** Connection/load errors and planning-run failures, stacked above the composer. */
function ChatBoxBanners(
  props: ChatBoxLayoutProps & { reconnect: ReturnType<typeof useProjectReconnect> },
): React.ReactElement {
  return (
    <>
      <ErrorBanner error={props.projectLoadError ?? props.error}
        reconnectState={props.reconnect.state} onRetryNow={props.reconnect.retryNow} />
      <PlanningFailureBanner
        projectId={props.projectId}
        sessionId={props.sessionId}
        message={props.planningFailureMessage ?? null}
        kind={props.planningFailureKind ?? null}
        onDismiss={props.onDismissPlanningFailure ?? noop}
        onStillWorking={props.onPlanningStillWorking} />
    </>
  );
}

const useChatActionMirror = (props: ChatBoxLayoutProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const activeSessionId = useAppSelector((state) => state.planning.activeProjectId === props.projectId
    ? state.planning.activeSessionId : null);
  const mirrorSessionId = props.sessionId ?? activeSessionId;
  const mirrorHidden = props.isSessionResolving || props.sessionLoadFailed || props.isProjectBootstrapping;

  useActionMirrorRegion(rootRef, {
    projectId: props.projectId, sessionId: mirrorSessionId,
    enabled: !props.isReadOnly && !mirrorHidden, priority: 1,
  });

  return { rootRef, mirrorSessionId, mirrorHidden };
};

// Keep the dock directly adjacent to its composer in the layout.
// eslint-disable-next-line max-lines-per-function
export function ChatBoxLayout(props: ChatBoxLayoutProps): React.ReactElement {
  const { rootRef, mirrorSessionId, mirrorHidden } = useChatActionMirror(props);
  // A failed or wedged run must not keep reading as "in progress" — that is what
  // left the tracker spinning and the composer locked with no way out. A slow
  // step the server still reports as running is not a failure: Stop stays.
  const hasPlanningFailure = Boolean(props.planningFailureMessage) && props.planningFailureKind !== 'slow';
  const isAnyAgentRunning = !hasPlanningFailure
    && (props.isPlanningRunning || Object.keys(props.activeAgents).length > 0);
  const userMessageCount = useMemo(
    () => props.messages.filter((m) => m.author === 'user').length,
    [props.messages],
  );
  const reconnect = useProjectReconnect(props.projectId, props.sessionId, props.projectLoadError);

  return (
    <div className="planning-left" ref={rootRef}>
      <MessagesContainer
        projectId={props.projectId}
        sessionId={props.sessionId}
        messages={props.messages} userDisplayName={props.userDisplayName}
        isAiTyping={props.isAiTyping} streamingDisplay={props.streamingDisplay}
        currentStreamingMsgId={props.currentStreamingMsgId}
        chatContainerRef={props.chatContainerRef} messagesEndRef={props.messagesEndRef}
        onScroll={props.onScroll}
        isProjectLoading={props.isProjectBootstrapping}
        isSessionResolving={props.isSessionResolving}
        sessionLoadFailed={props.sessionLoadFailed}
      />

      <ChatBoxBanners {...props} reconnect={reconnect} />
      <ChatQuotaQueue projectId={props.projectId} sessionId={mirrorSessionId} busy={props.isAiTyping} />
      <DocUploadChoiceWrapper sessionId={props.sessionId} />
      <DocProcessingBar />

      <ActiveTasksDock projectId={props.projectId} hidden={mirrorHidden}
        running={props.isPlanningRunning && !hasPlanningFailure} />
      <StickyPlanningActions projectId={props.projectId} sessionId={mirrorSessionId}
        isReadOnly={props.isReadOnly} hidden={mirrorHidden} />
      <ChatBoxInputArea
        isLoading={isChatInputLoading(props)}
        onInputChange={props.onInputChange} onSend={props.onSend}
        onAgentLogsClick={props.onOpenLogModal} inputRef={props.inputRef}
        projectId={props.projectId}
        sessionId={props.sessionId}
        isAnyAgentRunning={isAnyAgentRunning}
        isPlanningRunning={props.isPlanningRunning && !hasPlanningFailure}
        hasTodoTasks={props.hasTodoTasks}
        userMessageCount={userMessageCount}
        isReadOnly={props.isReadOnly}
      />

      <AgentLogModal isOpen={props.isLogModalOpen} onClose={props.onCloseLogModal}
        projectId={props.projectId || ''} />
    </div>
  );
}
